import React, { useEffect, useState } from 'react';
import { OrderTableContext, IOrderTableContext } from './OrderTableContext';
import { useAsyncData } from '@shared/react';
import { getMethods } from '../../api';
import { usePagination2 } from '@features/Pagination/usePagination';
import { getLogo } from '@widgets/AccountLevelPositionsTable/api';
import { combineFilterWithOp, joinFiltersWithComma } from '@shared/utils';
import { symbol } from 'prop-types';
import { useDrawer } from '@features/Modal';
import { OrderViewDrawer } from '@widgets/OrderTable/components/OrderViewDrawer';
import CloseSingleSecurityDrawerStore from '@widgets/OrderTable/store/account-refresh-store';
import { AssignPositionDrawer } from '@widgets/OrderTable/components/AssignPositionDrawer';

export interface ITransactionDesc {
    id: boolean;
    accountId: boolean;
    orderId: boolean;
    date: boolean;
    price: boolean;
    settlementDate: boolean;
    quantity: boolean;
    type: boolean;
    execId: boolean;
    processed: boolean;
    [key: string]: boolean;
}

export const OrderTableProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [orderIdentifier, setOrderIdentifier] = useState<string>('');
    const [sortFieldName, setSortFieldName] = useState('id');
    const [orderStatus, setOrderStatus] = useState<string | null>(null);
    const [orderType, setOrderType] = useState<string | null>(null);
    const [riaScreen, setRiaScreen] = useState<boolean | null>(true);

    const setIsDrawerClosed = CloseSingleSecurityDrawerStore((state) => state.setIsDrawerClosed);
    const [desc, setDesc] = useState<ITransactionDesc>({
        id: true,
        accountId: true,
        orderId: true,
        date: true,
        price: true,
        settlementDate: true,
        quantity: true,
        type: true,
        execId: true,
        processed: true,
    });

    const userStorage = localStorage.getItem('User');
    let userObject = null;
    let filteredAccounts = null;
    let filterToAdd: string;
    userObject = JSON.parse(userStorage ? userStorage : '');
    if (userObject?.accounts) {
        if (userObject.userRole === 4) {
            const accountId = userObject.accounts?.[0]?.id || null; // Get the first account's ID or null
            const subAccountIds = userObject.subAccounts?.map((sub: any) => sub.id) || []; // Get subAccount IDs or an empty array

            const combinedAccounts =
                subAccountIds.length > 0
                    ? [accountId, ...subAccountIds] // Include accountId and subAccountIds if subAccountIds is not empty
                    : [accountId]; // Only include accountId if subAccountIds is empty

            // Filter out null values
            filteredAccounts = combinedAccounts.filter((id) => id !== null);
            filterToAdd = combineFilterWithOp({
                AccountId: {
                    value: filteredAccounts.length && ` ${filteredAccounts.join(';')}`,
                    operator: ' #In',
                },
            });
        }
    }

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination2();

    const {
        data: OrderBody,
        handleRefresh: accountsRefresh,
        loading: orderLoading,
    } = useAsyncData(
        async () => {
            const filterKeys: any = {
                symbol: { value: orderIdentifier.toUpperCase(), operator: '=*' },
            };

            filterKeys.side = {
                value: orderType,
                operator: '=',
            };

            filterKeys.status = {
                value: orderStatus,
                operator: '=',
            };

            filterKeys.onlyRia = {
                value: riaScreen,
                operator: '=',
            };

            const { items, totalResults } = await getMethods({
                page: page,
                pageSize,
                sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
                filter: joinFiltersWithComma(combineFilterWithOp(filterKeys), filterToAdd ? filterToAdd : ''),
            });

            setTotalCount(totalResults);
            return items || [];
        },
        [],
        [pageSize, page, orderIdentifier, orderType, orderStatus, riaScreen],
    );

    const { data: arrayWithLogos, loading: logoLoading } = useAsyncData(
        async () => {
            return await Promise.all(
                OrderBody.map(async (item) => {
                    const res = await getLogo({ symbol: item.symbol });
                    return { link: res, symbol: item.symbol };
                }),
            );
        },
        [],
        [OrderBody],
    );

    const { open: openOrderViewDrawer } = useDrawer(OrderViewDrawer);

    const onOrderRowClick = (order: any) => {
        setIsDrawerClosed(false);
        openOrderViewDrawer({ order, accountsRefresh, localStorageKey: 'isUserhanged' });
    };

    const { open: openAssignPositionDrawer } = useDrawer((drawerProps) => (
        <AssignPositionDrawer
            positionsRefresh={accountsRefresh}
            order={drawerProps.order}
        />
    ));

    const onAssignPositionClick = (order: any) => {
        setIsDrawerClosed(false);
        openAssignPositionDrawer({ order });
    };

    const value: IOrderTableContext = {
        OrderBody,
        arrayWithLogos,
        logoLoading,
        paginationOptions,
        sortFieldName,
        desc,
        orderLoading,
        orderStatus,
        orderType,
        riaScreen,
        setRiaScreen,
        setOrderType,
        setOrderStatus,
        setOrderIdentifier,
        onOrderRowClick,
        onAssignPositionClick,
    };

    return <OrderTableContext.Provider value={value}>{children}</OrderTableContext.Provider>;
};
